import { Card, ContentLayout, ItemsContainer, TemplateWrapper } from "@2u-landing-pages/components";
import {
  ContentfulImageConfig,
  TypeBentoWithoutUnresolvableLinksResponse,
  TypeCardWithoutUnresolvableLinksResponse,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeDetail_calloutWithoutUnresolvableLinksResponse,
  TypeItems_containerWithoutUnresolvableLinksResponse,
  TypeMarqueeWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { Split as BricksSplit } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { Bento } from "../Bento";
import { DetailCallout } from "../DetailCallout";
import { Marquee } from "../Marquee";
import { BricksSplitProps, CustomCSSProperties, SplitProps } from "./Split.types";
// @ts-ignore
import styles from "./Split.module.scss";

export const Split: React.FC<SplitProps> = ({
  orientation,
  slots,
  section_background_color,
  section_background_image,
  section_top_padding,
  section_bottom_padding,
  secondary_background_color,
  phoneNumber,
  tuitionCost,
  alignment = orientation === "horizontal" ? "left-center" : "center-center",
  split_order_on_mobile = false,
  image_config,
  inspector,
}) => {
  const style: CustomCSSProperties = {};
  const splitProps: BricksSplitProps = {
    direction: split_order_on_mobile ? "column-reverse" : "column",
  };

  const isVertical = orientation === "vertical";

  if (orientation === "horizontal") {
    splitProps.lg = {
      direction: "row",
    };
  }

  if (secondary_background_color) style["--secondary-background-color"] = `var(${secondary_background_color})`;

  return (
    <TemplateWrapper
      padded
      backgroundColor={section_background_color}
      backgroundImage={section_background_image}
      backgroundImagePosition={alignment}
      image_config={image_config as unknown as ContentfulImageConfig}
      paddingTop={section_top_padding}
      paddingBottom={section_bottom_padding}
      style={style}
    >
      <BricksSplit {...splitProps} className={styles.split}>
        {slots?.map((slot) => {
          const classNames = cn(styles.slot, styles[`${slot?.sys?.contentType?.sys?.id}-${orientation}`], {
            [styles.slotFull]: slots?.length === 1,
          });

          switch (slot?.sys?.contentType?.sys?.id) {
            case "items_container":
              return (
                <ItemsContainer
                  key={slot?.sys?.id}
                  orientation={orientation}
                  className={classNames}
                  entry={slot as TypeItems_containerWithoutUnresolvableLinksResponse}
                />
              );
            case "content_full": {
              const { image, eyebrow, heading, subhead, body, image_aspect_ratio } = (
                slot as TypeContent_fullWithoutUnresolvableLinksResponse
              ).fields;
              const isImageOnly = Boolean(image && !eyebrow && !heading && !subhead && !body);

              return (
                <div
                  key={slot?.sys?.id}
                  className={cn(classNames, {
                    [styles.secondaryBackground]: Boolean(secondary_background_color),
                  })}
                >
                  <div
                    className={cn({
                      [styles.alignmentContainer]: Boolean(alignment) && !isVertical,
                      [styles.contentfulImageWrapper]: isImageOnly,
                    })}
                  >
                    <ContentLayout
                      className={cn(styles.contentLayout, {
                        [styles[alignment]]: Boolean(alignment) && !isVertical,
                      })}
                      content={slot as TypeContent_fullWithoutUnresolvableLinksResponse}
                      image_aspect_ratio={image_aspect_ratio}
                    />
                  </div>
                </div>
              );
            }
            case "bento":
              return (
                <Bento
                  key={slot?.sys?.id}
                  className={classNames}
                  embedded
                  {...(slot as TypeBentoWithoutUnresolvableLinksResponse)?.fields}
                  inspector={inspector}
                />
              );
            case "detail_callout":
              return (
                <DetailCallout
                  key={slot?.sys?.id}
                  className={classNames}
                  phoneNumber={phoneNumber}
                  tuitionCost={tuitionCost}
                  {...(slot as TypeDetail_calloutWithoutUnresolvableLinksResponse)?.fields}
                />
              );
            case "card":
              return (
                <Card
                  key={slot?.sys?.id}
                  className={classNames}
                  entry={slot as TypeCardWithoutUnresolvableLinksResponse}
                />
              );
            case "marquee":
              return (
                <Marquee
                  id={slot?.sys?.id}
                  key={slot?.sys?.id}
                  {...(slot as TypeMarqueeWithoutUnresolvableLinksResponse).fields}
                />
              );
            default:
              return null;
          }
        })}
      </BricksSplit>
    </TemplateWrapper>
  );
};
